import { myIcons } from "@/utils/icons";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useRef, useState } from "react";
import statics from "@/utils/statics.json";
const { iconBaseUrl } = statics;

export const Skills = () => {
    const { t } = useTranslation();

    const skills = t("skills", { returnObjects: true }) as any[];

    return (
        <div>
            <p style={{ fontSize: 25, marginBottom: 20, color: "var(--color-text)" }}>
                {t("skills-title")}
            </p>

            <div style={{ display: "flex", flexWrap: "wrap", gap: 15 }}>
                {skills.map((category) => (
                    <div
                        key={category.id}
                        style={{
                            flex: "1 1 300px",
                            background: "var(--color-bg-secondary)",
                            padding: 20,
                            borderRadius: 10,
                            color: "var(--color-text)"
                        }}
                    >
                        <p style={{ marginBottom: 10 }}>{category.title}</p>

                        <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                            {category.items.map((item: any, index: number) => (
                                <SkillItem key={item.id} item={item} index={index} />
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

const SkillItem = ({ item, index }: { item: any; index: number }) => {
    const [hovered, setHovered] = useState(false);
    const [cardPosition, setCardPosition] = useState<'left' | 'center' | 'right'>('center');
    const ref = useRef<HTMLDivElement>(null);
    const timeoutRef = useRef<number | null>(null);

    const icon = item.id ? `${iconBaseUrl}${item.id}.svg` : "";


    const handleMouseEnter = () => {
        // Eğer önceki timeout varsa iptal et
        if (timeoutRef.current) clearTimeout(timeoutRef.current);

        // 0.5 saniye sonra hover aktif olacak
        timeoutRef.current = window.setTimeout(() => {
            setHovered(true);

            // Pozisyon ayarı burada da olabilir
            if (ref.current) {
                const rect = ref.current.getBoundingClientRect();
                if (rect.left < 150) setCardPosition('left');
                else if (window.innerWidth - rect.right < 150) setCardPosition('right');
                else setCardPosition('center');
            }
        }, 250);
    };

    const handleMouseLeave = () => {
        // Timeout varsa iptal et
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = null;

        // Hover anında kaldır
        setHovered(false);
    };
    const isHighlight = Boolean(item.highlight);
    return (
        <motion.div
            initial={{ scale: 0 }}
            transition={{ duration: 0.4, delay: index * 0.15, ease: "easeOut" }}
            style={{ position: "relative", display: "flex" }}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            ref={ref}
            whileInView={{ scale: 1 }}
            viewport={{ once: true, amount: 1 }}
        >
            <motion.div
                animate={
                    isHighlight
                        ? {
                            background: [
                                "linear-gradient(var(--color-bg-secondary), var(--color-bg-secondary)) padding-box, linear-gradient(0deg, #ff00cc, #3333ff, #00ffcc, #ff00cc) border-box",
                                "linear-gradient(var(--color-bg-secondary), var(--color-bg-secondary)) padding-box, linear-gradient(360deg, #ff00cc, #3333ff, #00ffcc, #ff00cc) border-box",
                            ],
                        }
                        : {}
                }
                transition={
                    isHighlight
                        ? { duration: 2, repeat: Infinity, ease: "linear" }
                        : {}
                }
                style={{
                    display: "flex",
                    width: "50px",
                    height: "50px",
                    padding: "5px",
                    borderRadius: "5px",
                    alignItems: "center",
                    justifyContent: "center",
                    border: isHighlight ? "2px solid transparent" : "1px dashed #acacacff",
                    background: isHighlight
                        ? "linear-gradient(var(--color-bg-secondary), var(--color-bg-secondary)) padding-box, linear-gradient(90deg, #ff00cc, #3333ff, #00ffcc, #ff00cc) border-box"
                        : "var(--color-bg-secondary)",
                }}
            >
                <img
                    style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                    src={icon} alt="" />
            </motion.div>

            <AnimatePresence>
                {hovered && (
                    <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: -20 }}
                        exit={{ opacity: 0, y: -10 }}
                        style={{
                            position: "absolute",
                            top: "-60px",
                            left: cardPosition === 'left' ? '-20px' : cardPosition === 'right' ? 'auto' : '-100px',
                            right: cardPosition === 'right' ? '-20px' : 'auto',
                            transform: cardPosition === 'center' ? "translateX(-50%)" : "none",
                            backgroundColor: 'var(--color-bg-secondary)',
                            padding: "10px",
                            borderRadius: "5px",
                            boxShadow: "0 2px 10px rgba(0,0,0,0.2)",
                            whiteSpace: "normal",
                            zIndex: 10,
                        }}
                    >
                        <SkillCard item={item} icon={icon} />
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    );
};

const SkillCard = ({ item, icon }: { item: any; icon: string }) => {
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <img src={icon} width={30} height={30} />
                <p>{item.title}</p>
            </div>
            <ul
                style={{
                    paddingLeft: '20px',
                    listStyleType: 'disc',
                    maxWidth: '250px',
                    whiteSpace: 'normal',
                    overflowWrap: 'break-word',
                }}
            >
                {item.desc.map((d: string, i: number) => (
                    <li
                        key={i}
                        style={{ fontSize: 12 }}
                        dangerouslySetInnerHTML={{ __html: d }}
                    />
                ))}
            </ul>
            <div style={{ display: 'flex', gap: '5px' }}>
                {Array.from({ length: 10 }).map((_, index) => (
                    <motion.div
                        key={index}
                        initial={{ backgroundColor: '#ccc', scaleX: 0 }}
                        animate={{
                            backgroundColor: index < item.percent ? '#4caf50' : '#ccc',
                            scaleX: index < item.percent ? 1 : 1
                        }}
                        transition={{ duration: 0.5, delay: index * 0.05 }}
                        style={{
                            flex: 1,
                            height: '5px',
                            width: '20px',
                            backgroundColor: index < item.percent ? '#4caf50' : '#ccc',
                            borderRadius: '4px',
                        }}
                    />
                ))}
            </div>
        </div>
    );
};
